import type { AdminReportVoiceNoteDto } from '@carcheck/shared';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type SyntheticEvent,
} from 'react';
import { useTranslation } from 'react-i18next';

import { tokens } from '../../../design';
import { formatClipDuration } from '../_lib/formatClipDuration';
import { isSummaryVoiceNote, sortVoiceNotes, voiceNoteStepTitle } from '../_lib/voiceNotes';

/**
 * «Голосовые заметки · N» section of the report media tab (AUT-429,
 * mockup `report-media-voice.html`).
 *
 *   - Rows in inspection-route order, «Итог» last (`sortVoiceNotes`).
 *   - A READY note with a URL plays through a plain `<audio controls>`;
 *     `preload="none"` — a moderator scanning the tab shouldn't pull every
 *     clip. No `crossOrigin`: the signed S3 URL is a plain media GET and a
 *     CORS request would fail against a bucket without CORS headers.
 *   - `processing` (or ready but no URL yet) → «Обрабатывается…»;
 *     `failed` → «Не удалось обработать». Neither renders an `<audio>`.
 *   - «только подборщику» chip marks `inspector_only` notes (not in the
 *     customer's report).
 *   - One clip at a time: a capture-phase `play` listener on the section
 *     pauses every other `<audio>` in it.
 *   - `onAudioError(noteId)` lets the parent re-mint expired URLs; the
 *     `key` includes the URL so a fresh one remounts the element.
 */
export interface VoiceNotesSectionProps {
  notes: readonly AdminReportVoiceNoteDto[];
  onAudioError: (noteId: string) => void;
}

const SECTION_TITLE_SX = {
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.6px',
  color: tokens.text.tertiary,
  mb: 1.5,
};

const STATUS_SX = { display: 'flex', alignItems: 'center', gap: 1, fontSize: 14 } as const;

const VoiceNoteBody = ({
  note,
  title,
  onAudioError,
}: {
  note: AdminReportVoiceNoteDto;
  title: string;
  onAudioError: (noteId: string) => void;
}): ReactElement => {
  const { t } = useTranslation();
  // A refetch inside the server's URL-stability window hands back the SAME
  // URL, so the key alone would leave an errored player dead. Each error
  // remounts it; with preload="none" nothing loads until the next play.
  const [attempt, setAttempt] = useState(0);

  if (note.status === 'failed') {
    return (
      <Typography sx={{ ...STATUS_SX, color: 'error.main' }}>
        <ErrorOutlineIcon fontSize="small" />
        {t('reports.mediaTab.voiceFailed')}
      </Typography>
    );
  }
  if (note.status === 'processing' || note.signedUrl === null) {
    return (
      <Typography sx={{ ...STATUS_SX, color: 'text.secondary' }}>
        <CircularProgress size={14} color="inherit" />
        {t('reports.mediaTab.voiceProcessing')}
      </Typography>
    );
  }
  return (
    <Box
      component="audio"
      key={`${note.id}:${note.signedUrl}:${attempt}`}
      controls
      preload="none"
      src={note.signedUrl}
      aria-label={t('reports.mediaTab.voicePlayerLabel', { label: title })}
      onError={() => {
        setAttempt((n) => n + 1);
        onAudioError(note.id);
      }}
      sx={{ width: '100%', display: 'block' }}
    >
      {t('reports.mediaTab.voiceUnsupported')}
    </Box>
  );
};

const VoiceNoteRow = ({
  note,
  onAudioError,
}: {
  note: AdminReportVoiceNoteDto;
  onAudioError: (noteId: string) => void;
}): ReactElement => {
  const { t } = useTranslation();
  // `stepKey === null` is already in isSummaryVoiceNote; repeated here so the
  // compiler narrows `note.stepKey` to a string in the step branch.
  const title =
    isSummaryVoiceNote(note) || note.stepKey === null
      ? t('reports.mediaTab.voiceSummary')
      : t('reports.mediaTab.voiceStep', { step: voiceNoteStepTitle(note.stepKey, t) });
  const duration = formatClipDuration(note.durationSec);

  return (
    <Box
      data-testid="voice-note-row"
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 2fr) minmax(0, 3fr)' },
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1.75,
        '& + &': { borderTop: '1px solid', borderColor: 'divider' },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{title}</Typography>
        {duration !== null || note.visibility === 'inspector_only' ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            {duration !== null ? (
              <Typography sx={{ fontFamily: 'monospace', fontSize: 12, color: 'text.secondary' }}>
                {duration}
              </Typography>
            ) : null}
            {note.visibility === 'inspector_only' ? (
              <Chip
                size="small"
                variant="outlined"
                icon={<LockOutlinedIcon />}
                label={t('reports.mediaTab.voiceOwnerOnly')}
              />
            ) : null}
          </Box>
        ) : null}
      </Box>
      <VoiceNoteBody note={note} title={title} onAudioError={onAudioError} />
    </Box>
  );
};

export const VoiceNotesSection = ({
  notes,
  onAudioError,
}: VoiceNotesSectionProps): ReactElement => {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);
  const sorted = useMemo(() => sortVoiceNotes(notes), [notes]);

  // `play` doesn't bubble, but React routes media events through both
  // phases of its own tree, so the capture handler sees every row's clip.
  const onPlayCapture = useCallback((event: SyntheticEvent): void => {
    sectionRef.current?.querySelectorAll('audio').forEach((audio) => {
      if (audio !== event.target && !audio.paused) audio.pause();
    });
  }, []);

  return (
    <Box component="section" ref={sectionRef} onPlayCapture={onPlayCapture}>
      <Typography sx={SECTION_TITLE_SX} data-testid="report-detail-voice-count">
        {t('reports.mediaTab.voiceTitle', { count: notes.length })}
      </Typography>
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
        {sorted.map((note) => (
          <VoiceNoteRow key={note.id} note={note} onAudioError={onAudioError} />
        ))}
      </Box>
    </Box>
  );
};
